import { mapGetters } from 'vuex'
import config from 'config'

import store from '@vue-storefront/core/store'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import { htmlDecode } from '@vue-storefront/core/filters'
import { currentStoreView, localizedRoute } from '@vue-storefront/core/lib/multistore'
import { CompareProduct } from '@vue-storefront/core/modules/compare/components/Product.ts'
import { AddToCompare } from '@vue-storefront/core/modules/compare/components/AddToCompare.ts'
import { isOptionAvailableAsync } from '@vue-storefront/core/modules/catalog/helpers/index'
import omit from 'lodash-es/omit'
import Composite from '@vue-storefront/core/mixins/composite'
import { Logger } from '@vue-storefront/core/lib/logger'
import { formatProductLink } from '@vue-storefront/core/modules/url/helpers'
import { s3Path } from '@vue-storefront/core/helpers'

export default {
  name: 'Product',
  mixins: [Composite, AddToCompare, CompareProduct],
  data () {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters({
      product: 'product/productCurrent',
      originalProduct: 'product/productOriginal',
      parentProduct: 'product/productParent',
      attributesByCode: 'attribute/attributeListByCode',
      attributesById: 'attribute/attributeListById',
      breadcrumbs: 'product/breadcrumbs',
      configuration: 'product/currentConfiguration',
      options: 'product/currentOptions',
      category: 'category/getCurrentCategory',
      gallery: 'product/productGallery',
      currentPartner: 'partners/getCurrentPartner',
      metaInformation: 'configs/getMetaInformation'
    }),
    isDisabledProduct () {
      return this.product.status === 2 || !this.currentPartner.active || !this.currentPartner.enabled;
    },
    productName () {
      return this.product ? this.product.name : ''
    },
    productId () {
      return this.product ? this.product.entity_id : ''
    },
    productFinalPrice () {
      return this.product.final_price ? this.product.final_price : ''
    },
    productDescription () {
      return this.product.description ? this.product.description.replace(/<\/?[^>]+>/gi, '') : 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
    },
    offlineImage () {
      return {
        src: this.getThumbnail(this.product.image, config.products.thumbnails.width, config.products.thumbnails.height),
        error: this.getThumbnail(this.product.image, config.products.thumbnails.width, config.products.thumbnails.height),
        loading: this.getThumbnail(this.product.image, config.products.thumbnails.width, config.products.thumbnails.height)
      }
    },
    image () {
      return this.gallery.length ? this.gallery[0] : false
    },
    customAttributes () {
      return Object.values(this.attributesByCode).filter(a => {
        return a.is_visible && a.is_user_defined && (parseInt(a.is_visible_on_front) || a.is_visible_on_front === true) && this.product[a.attribute_code]
      })
    },
    currentStore () {
      return currentStoreView()
    },
    productLink () {
      return formatProductLink(this.product, currentStoreView().storeCode, this.$route.params.store, this.$route.params.locationCode)
    },
    defaultProductImageUrl () {
      return this.ogMetaImage ? this.ogMetaImage : ''
    },
    productImageUrl () {
      const thumbnailConfig = config && config.images && config.images.product_thumbnail;
      const width = thumbnailConfig && thumbnailConfig.width ? thumbnailConfig.width : null;
      const height = thumbnailConfig && thumbnailConfig.height ? thumbnailConfig.height : null;

      return s3Path(this.product.image, width, height, true);
    },
    ogMetaImage () {
      return this.metaInformation.find(meta => meta.key === 'og_image') ? this.metaInformation.find(meta => meta.key === 'og_image').value : ''
    },
    getHostPath () {
      return config.canonical ? config.canonical.url : ''
    }
  },
  async asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data
    EventBus.$emit('product-before-load', { store: store, route: route })
    if (context) context.output.cacheTags.add(`product`)
    let partner = route.params.store ? route.params.store : false

    // metaInfo() and the displayMode-gated template read currentPartner; App.vue's
    // store watcher doesn't fire for the initial route.
    if (partner) {
      await store.dispatch('partners/ensureCurrentPartner', { code: partner, locationCode: route.params.locationCode })
    }

    return store.dispatch('product/fetchAsync', { partner: partner, parentSku: route.params.parentSku, childSku: route && route.params && route.params.childSku ? route.params.childSku : null })
  },
  beforeRouteUpdate (to, from, next) {
    this.validateRoute(to) // TODO: remove because client-entry.ts is executing `asyncData` anyway
    next()
  },
  beforeDestroy () {
    this.$bus.$off('product-after-removevariant')
    this.$bus.$off('filter-changed-product')
    this.$bus.$off('product-after-priceupdate', this.onAfterPriceUpdate)
    this.$bus.$off('product-after-customoptions')
    this.$bus.$off('product-after-bundleoptions')
    if (config.usePriceTiers) {
      this.$bus.$off('user-after-loggedin', this.onUserPricesRefreshed)
      this.$bus.$off('user-after-logout', this.onUserPricesRefreshed)
    }
  },
  async beforeMount () {
    this.$bus.$on('product-after-removevariant', this.onAfterVariantChanged)
    this.$bus.$on('product-after-priceupdate', this.onAfterPriceUpdate)
    this.$bus.$on('filter-changed-product', this.onAfterFilterChanged)
    this.$bus.$on('product-after-customoptions', this.onAfterCustomOptionsChanged)
    this.$bus.$on('product-after-bundleoptions', this.onAfterBundleOptionsChanged)
    if (config.usePriceTiers) {
      this.$bus.$on('user-after-loggedin', this.onUserPricesRefreshed)
      this.$bus.$on('user-after-logout', this.onUserPricesRefreshed)
    }
    this.onStateCheck()
  },
  watch: {
    product: {
      handler () {
        if (this.product.hasOwnProperty('selected_options')) {
          this.onAfterCustomOptionsChanged({ optionValues: this.product.selected_options })
        } else if (this.product.hasOwnProperty('custom_options')) {
          this.product.custom_options.map(option => {
            if (option.is_require) {
              this.onAfterCustomOptionsChanged({ optionValues: option.values })
            }
          })
        }
        if (this.product && this.product.hasOwnProperty('category') && this.product.category[0].name === '') {
          this.$store.dispatch('product/setupBreadcrumbs', {product: this.product})
          this.$forceUpdate()
        }
      }
    }
  },
  methods: {
    validateRoute (route = this.$route) {
      if (!this.loading) {
        this.loading = true
        this.$store.dispatch('product/fetchAsync', { parentSku: route.params.parentSku, childSku: route && route.params && route.params.childSku ? route.params.childSku : null }).then(res => {
          this.loading = false
          this.defaultOfflineImage = this.product.image
          this.onStateCheck()
        }).catch((err) => {
          this.loading = false
          Logger.error(err)()
          this.notifyOutStock()
          this.$router.back()
        })
      } else {
        Logger.error('Error with loading = true in Product.vue; Reload page')()
      }
    },
    isOptionAvailable (option) { // check if the option is available
      let currentConfig = Object.assign({}, this.configuration)
      currentConfig[option.attribute_code] = option
      return isOptionAvailableAsync(this.$store, { product: this.product, configuration: currentConfig })
    },
    onAfterCustomOptionsChanged (payload) {
      if (this.product && payload.product && this.product.entity_id === payload.product.entity_id) {
        let priceDelta = 0
        let priceDeltaInclTax = 0
        let optionsToProduct = []
        for (const optionValue of Object.values(payload.optionValues)) {
          optionsToProduct.push(optionValue)
          if (typeof optionValue === 'object' && parseInt(optionValue.option_type_id) > 0) {
            if (optionValue.price_type === 'fixed' && optionValue.price !== 0) {
              priceDeltaInclTax += optionValue.price * (optionValue.hasOwnProperty('qty') ? optionValue.qty : 1)
            }
            if (optionValue.price_type === 'percent' && optionValue.price !== 0) {
              priceDelta += ((optionValue.price / 100) * this.originalProduct.price)
              priceDeltaInclTax += ((optionValue.price / 100) * this.originalProduct.priceInclTax)
            }
          }
        }
        this.product.selected_options = optionsToProduct
        this.product.price = this.originalProduct.price + priceDelta
        this.product.priceInclTax = this.originalProduct.priceInclTax + priceDeltaInclTax
      }
    },
    onStateCheck () {
      this.product.qty = this.product.stock.min_sale_qty
    },
    onAfterPriceUpdate (product) {
      if (product.sku === this.product.sku) {
        // join selected variant object to the store
        this.$store.dispatch('product/setCurrent', omit(product, ['name']))
          .catch(err => Logger.error({
            info: 'Dispatch product/setCurrent in Product.vue',
            err
          }))
      }
    },
    onAfterVariantChanged (payload) {
      this.$forceUpdate()
    },
    onAfterFilterChanged (filterOption) {
      this.$bus.$emit('product-before-configure', { filterOption: filterOption, configuration: this.configuration })
      const prevOption = this.configuration[filterOption.attribute_code]
      this.configuration[filterOption.attribute_code] = filterOption
      this.$forceUpdate() // this is to update the available options regarding current selection
      this.$store.dispatch('product/configure', {
        product: this.product,
        configuration: this.configuration,
        selectDefaultVariant: true,
        fallbackToDefaultWhenNoAvailable: false,
        setProductErorrs: true
      }).then((selectedVariant) => {
        if (config.products.setFirstVarianAsDefaultInURL) {
          this.$router.push({params: { childSku: selectedVariant.sku }})
        }
        if (!selectedVariant) {
          if (typeof prevOption !== 'undefined' && prevOption) {
            this.configuration[filterOption.attribute_code] = prevOption
          } else {
            delete this.configuration[filterOption.attribute_code]
          }
          this.notifyWrongAttributes()
        }
      }).catch(err => Logger.error({
        info: 'Dispatch product/configure in Product.vue',
        err
      }))
    },
    /**
     * Reload product to get correct prices (including tier prices for group)
     */
    onUserPricesRefreshed () {
      if (this.$route.params.parentSku) {
        this.$store.dispatch('product/reset')
        this.$bus.$emit('product-before-load', { store: this.$store, route: this.$route })
        this.$store.dispatch('product/single', {
          options: {
            sku: this.$route.params.parentSku,
            childSku: this.$route && this.$route.params && this.$route.params.childSku ? this.$route.params.childSku : null
          },
          skipCache: true
        })
      }
    }
  },
  metaInfo () {
    if (!this.currentPartner) {
      return {
        title: 'Доставка товарів звідусіль',
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content: 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
          }
        ]
      }
    }
    let text = this.currentPartner.display_mode === 'food'
      ? '. Доставка до 60 хвилин'
      : '. Доставка якнайшвидше або у вибраний час'

    let currentProductMeta = [
      {
        vmid: 'description',
        name: 'description',
        content: this.productDescription
      },
      {
        vmid: 'og:type',
        property: 'og:type',
        content: 'og:product'
      },
      {
        vmid: 'og:title',
        property: 'og:title',
        content: this.productName
      },
      {
        vmid: 'og:image',
        property: 'og:image',
        content: this.product.image ? this.productImageUrl : this.defaultProductImageUrl
      },
      {
        vmid: 'og:description',
        property: 'og:description',
        content: this.productDescription
      },
      {
        vmid: 'og:url',
        property: 'og:url',
        content: config.baseUrl + this.productLink
      },
      {
        vmid: 'product:price:amount',
        property: 'product:price:amount',
        content: this.productFinalPrice
      },
      {
        vmid: 'product:price:currency',
        property: 'product:price:currency',
        content: 'грн'
      }
    ]

    return {
      title: `${this.productName} з ${this.currentPartner.title}${text}`,
      meta: currentProductMeta,
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: this.product.image ? this.productImageUrl : this.defaultProductImageUrl
        },
        // K2-2985: was its own hardcoded /p/{parentSku}/{slug} template, independent
        // of productLink() below - reuse productLink so the canonical tag can never
        // drift from the actual short/long URL formatProductLink()/localizedRoute()
        // decide to generate.
        {rel: 'canonical', href: `${this.getHostPath}${this.productLink}`}
      ]
    }
  }
}
